import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { RestartAlt as ResetIcon, Save as SaveIcon, Search as SearchIcon } from "@mui/icons-material";
import { apiService } from "../services/api";
import { Media } from "../types";

const filenameLikePattern = /\.(mp4|avi|mov|mkv|webm)$/i;

function getEditableName(media: Media): string {
    return media.originalName || media.name || "";
}

function isFilenameLike(value: string): boolean {
    const normalized = value.trim();
    return filenameLikePattern.test(normalized) || /^[0-9]+_[A-Za-z0-9_.-]+$/.test(normalized);
}

function buildHierarchy(media: Media, name: string): string {
    const parts: string[] = [];
    if (media.grandparent?.name) {
        parts.push(media.grandparent.name);
    }
    if (media.parent?.name) {
        parts.push(media.parent.name);
    }
    if (name) {
        parts.push(name);
    }
    return parts.length > 0 ? parts.join(" > ") : name;
}

function getMediaLevelLabel(media: Media): string {
    if (media.grandparent) {
        return "Серия/медиа";
    }
    if (media.parent) {
        return "Сезон/раздел";
    }
    return "Фильм/сериал";
}

function formatParentOptionLabel(media: Media): string {
    const hierarchy = media.hierarchy || buildHierarchy(media, getEditableName(media));
    const supplier = media.supplier?.name || media.supplierId || "без поставщика";
    return `#${media.id} ${hierarchy} · ${supplier}`;
}

const MediaNameManagement: React.FC = () => {
    const [mediaList, setMediaList] = useState<Media[]>([]);
    const [draftNames, setDraftNames] = useState<Record<number, string>>({});
    const [draftParentIds, setDraftParentIds] = useState<Record<number, number | null>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filenameOnly, setFilenameOnly] = useState(true);
    const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const fetchMediaList = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await apiService.getMediaRenameList();
            const mediaArray = Array.isArray(data) ? data : [];
            setMediaList(mediaArray);
            setDraftNames(
                mediaArray.reduce<Record<number, string>>((acc, media) => {
                    acc[media.id] = getEditableName(media);
                    return acc;
                }, {}),
            );
            setDraftParentIds(
                mediaArray.reduce<Record<number, number | null>>((acc, media) => {
                    acc[media.id] = media.parentId ?? null;
                    return acc;
                }, {}),
            );
        } catch (err: any) {
            console.error("Ошибка загрузки списка медиа:", err);
            setError(err.response?.data?.message || "Ошибка загрузки списка медиа");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchMediaList();
    }, []);

    const mediaById = useMemo(() => new Map(mediaList.map((media) => [media.id, media])), [mediaList]);

    const parentBaseOptions = useMemo(() => mediaList.filter((media) => !media.grandparent), [mediaList]);

    const isDescendantOf = (candidate: Media, mediaId: number): boolean => {
        const visitedIds = new Set<number>();
        let parentId = candidate.parentId ?? null;

        while (parentId) {
            if (parentId === mediaId) {
                return true;
            }
            if (visitedIds.has(parentId)) {
                return false;
            }
            visitedIds.add(parentId);
            parentId = mediaById.get(parentId)?.parentId ?? null;
        }

        return false;
    };

    const getParentOptions = (media: Media): Media[] => {
        const supplierId = media.supplierId ?? null;
        return parentBaseOptions.filter((option) => {
            const parentSupplierId = option.supplierId ?? null;
            if (option.id === media.id) {
                return false;
            }
            if (parentSupplierId !== null && parentSupplierId !== supplierId) {
                return false;
            }
            return !isDescendantOf(option, media.id);
        });
    };

    const filteredMedia = useMemo(() => {
        const searchLower = searchTerm.trim().toLowerCase();
        return mediaList.filter((media) => {
            const currentName = getEditableName(media);
            const draftName = draftNames[media.id] ?? currentName;
            if (filenameOnly && !isFilenameLike(currentName)) {
                return false;
            }
            if (!searchLower) {
                return true;
            }
            return [
                String(media.id),
                currentName,
                draftName,
                media.hierarchy || "",
                media.parent?.name || "",
                media.grandparent?.name || "",
                String(media.parentId || ""),
                media.supplier?.name || "",
                String(media.supplierId || ""),
            ].some((value) => value.toLowerCase().includes(searchLower));
        });
    }, [draftNames, filenameOnly, mediaList, searchTerm]);

    const paginatedMedia = useMemo(() => {
        const startIndex = (page - 1) * rowsPerPage;
        return filteredMedia.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredMedia, page, rowsPerPage]);

    const totalPages = Math.ceil(filteredMedia.length / rowsPerPage);
    const filenameLikeCount = mediaList.filter((media) => isFilenameLike(getEditableName(media))).length;
    const changedCount = mediaList.filter((media) => {
        const currentName = getEditableName(media);
        const currentParentId = media.parentId ?? null;
        const draftParentId = draftParentIds[media.id] ?? currentParentId;
        return (draftNames[media.id] ?? currentName).trim() !== currentName.trim() || draftParentId !== currentParentId;
    }).length;

    const updateDraftName = (mediaId: number, value: string) => {
        setDraftNames((prev) => ({
            ...prev,
            [mediaId]: value,
        }));
    };

    const updateDraftParentId = (mediaId: number, value: number | null) => {
        setDraftParentIds((prev) => ({
            ...prev,
            [mediaId]: value,
        }));
    };

    const resetDraftName = (media: Media) => {
        setDraftNames((prev) => ({
            ...prev,
            [media.id]: getEditableName(media),
        }));
        setDraftParentIds((prev) => ({
            ...prev,
            [media.id]: media.parentId ?? null,
        }));
    };

    const saveMediaName = async (media: Media) => {
        const nextName = (draftNames[media.id] || "").trim();
        const currentName = getEditableName(media).trim();
        const currentParentId = media.parentId ?? null;
        const nextParentId = draftParentIds[media.id] ?? currentParentId;
        if (!nextName) {
            setError("Название медиа не может быть пустым");
            setInfo(null);
            return;
        }
        if (nextName === currentName && nextParentId === currentParentId) {
            return;
        }

        try {
            setSavingIds((prev) => new Set(prev).add(media.id));
            setError(null);
            setInfo(null);
            const updated = await apiService.updateMediaName(media.id, nextName, nextParentId);
            const savedName = updated?.name || nextName;
            const savedParentId = updated?.parentId ?? nextParentId;
            const selectedParent = savedParentId ? mediaById.get(savedParentId) : null;
            const fallbackParent = selectedParent
                ? {
                      id: selectedParent.id,
                      name: getEditableName(selectedParent) || "Без названия",
                      parentId: selectedParent.parentId ?? null,
                  }
                : null;
            const fallbackGrandparent = selectedParent?.parent
                ? {
                      id: selectedParent.parent.id,
                      name: selectedParent.parent.name || "Без названия",
                  }
                : null;
            setMediaList((prev) =>
                prev.map((item) =>
                    item.id === media.id
                        ? {
                              ...item,
                              name: savedName,
                              originalName: savedName,
                              parentId: savedParentId,
                              parent: updated?.parent ?? fallbackParent,
                              grandparent: updated?.grandparent ?? fallbackGrandparent,
                              hierarchy:
                                  updated?.hierarchy ||
                                  buildHierarchy(
                                      {
                                          ...item,
                                          parent: updated?.parent ?? fallbackParent,
                                          grandparent: updated?.grandparent ?? fallbackGrandparent,
                                      },
                                      savedName,
                                  ),
                              updatedAt: new Date().toISOString(),
                          }
                        : item,
                ),
            );
            setDraftNames((prev) => ({
                ...prev,
                [media.id]: savedName,
            }));
            setDraftParentIds((prev) => ({
                ...prev,
                [media.id]: savedParentId,
            }));
            setInfo(`Медиа #${media.id} обновлено`);
        } catch (err: any) {
            console.error("Ошибка переименования медиа:", err);
            setError(err.response?.data?.message || "Ошибка переименования медиа");
        } finally {
            setSavingIds((prev) => {
                const next = new Set(prev);
                next.delete(media.id);
                return next;
            });
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error && mediaList.length === 0) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" component="h1" gutterBottom>
                Переименование медиа
            </Typography>

            <Alert severity="info" sx={{ mb: 3, mt: 2 }}>
                Меняйте поле «Название медиа», чтобы в статистике вместо технических файлов вроде
                8739_PrVSrMp4_50_Ottenkov_Braka_S01E01.mp4 отображались нормальные названия. Если файл должен попасть
                в сериал или сезон, выберите ему родителя и сохраните вместе с названием серии.
            </Alert>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                        <TextField
                            placeholder="Поиск по ID, названию или поставщику..."
                            value={searchTerm}
                            onChange={(event) => {
                                setSearchTerm(event.target.value);
                                setPage(1);
                            }}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ minWidth: 320 }}
                        />
                        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={filenameOnly}
                                        onChange={(event) => {
                                            setFilenameOnly(event.target.checked);
                                            setPage(1);
                                        }}
                                    />
                                }
                                label="Только похожие на файлы"
                            />
                            <Chip label={`Похожи на файлы: ${filenameLikeCount}`} variant="outlined" />
                            <Chip label={`Изменено: ${changedCount}`} color={changedCount > 0 ? "warning" : "default"} />
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {info && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
                    {info}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <TableContainer component={Paper} sx={{ maxHeight: 680 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 80 }}>ID</TableCell>
                                    <TableCell sx={{ minWidth: 320 }}>Текущее отображение</TableCell>
                                    <TableCell sx={{ minWidth: 360 }}>Родитель</TableCell>
                                    <TableCell sx={{ minWidth: 360 }}>Новое название / название серии</TableCell>
                                    <TableCell sx={{ width: 150 }}>Поставщик</TableCell>
                                    <TableCell align="right" sx={{ width: 190 }}>Действия</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredMedia.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="text.secondary">
                                                {searchTerm ? "Медиа не найдены" : "Нет медиа для переименования"}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedMedia.map((media) => {
                                        const currentName = getEditableName(media);
                                        const draftName = draftNames[media.id] ?? currentName;
                                        const currentParentId = media.parentId ?? null;
                                        const draftParentId = draftParentIds[media.id] ?? currentParentId;
                                        const nameChanged = draftName.trim() !== currentName.trim();
                                        const parentChanged = draftParentId !== currentParentId;
                                        const changed = nameChanged || parentChanged;
                                        const invalid = draftName.trim().length === 0 || draftName.trim().length > 1024;
                                        const saving = savingIds.has(media.id);
                                        const selectedParent = draftParentId ? mediaById.get(draftParentId) || null : null;
                                        const parentOptions = getParentOptions(media);
                                        const visibleParentOptions =
                                            selectedParent && !parentOptions.some((option) => option.id === selectedParent.id)
                                                ? [selectedParent, ...parentOptions]
                                                : parentOptions;

                                        return (
                                            <TableRow key={media.id}>
                                                <TableCell>{media.id}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ maxWidth: "100%", wordBreak: "break-word" }}>
                                                        <Typography variant="body1" fontWeight={500}>
                                                            {media.hierarchy || currentName || "Без названия"}
                                                        </Typography>
                                                        <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                                            <Chip label={getMediaLevelLabel(media)} size="small" variant="outlined" />
                                                            {media.parentId && (
                                                                <Chip label={`parent #${media.parentId}`} size="small" variant="outlined" />
                                                            )}
                                                        </Box>
                                                        {isFilenameLike(currentName) && (
                                                            <Chip
                                                                label="похоже на filename"
                                                                size="small"
                                                                color="warning"
                                                                variant="outlined"
                                                                sx={{ mt: 1 }}
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Autocomplete
                                                        options={visibleParentOptions}
                                                        value={selectedParent}
                                                        onChange={(_, value) => updateDraftParentId(media.id, value?.id ?? null)}
                                                        getOptionLabel={formatParentOptionLabel}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        noOptionsText="Нет доступных родителей"
                                                        disabled={saving}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Родитель"
                                                                placeholder="Без родителя"
                                                                size="small"
                                                                helperText={
                                                                    parentChanged
                                                                        ? "Будет сохранено в parentId"
                                                                        : selectedParent
                                                                          ? "Текущий родитель"
                                                                          : "Без родителя"
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        value={draftName}
                                                        onChange={(event) => updateDraftName(media.id, event.target.value)}
                                                        fullWidth
                                                        multiline
                                                        minRows={1}
                                                        maxRows={4}
                                                        size="small"
                                                        error={invalid}
                                                        helperText={
                                                            invalid
                                                                ? "От 1 до 1024 символов"
                                                                : nameChanged && parentChanged
                                                                  ? "Будет сохранено name и parentId"
                                                                  : nameChanged
                                                                  ? "Будет сохранено в поле name"
                                                                  : parentChanged
                                                                    ? "Название не изменится, изменится parentId"
                                                                  : " "
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>{media.supplier?.name || media.supplierId || "Нет"}</TableCell>
                                                <TableCell align="right">
                                                    <Box display="flex" justifyContent="flex-end" gap={1}>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<ResetIcon />}
                                                            disabled={!changed || saving}
                                                            onClick={() => resetDraftName(media)}
                                                        >
                                                            Сброс
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                                            disabled={!changed || invalid || saving}
                                                            onClick={() => void saveMediaName(media)}
                                                        >
                                                            Сохранить
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {filteredMedia.length > 0 && (
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                Показано {paginatedMedia.length} из {filteredMedia.length} медиа (всего: {mediaList.length})
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>На странице</InputLabel>
                                    <Select
                                        value={rowsPerPage}
                                        label="На странице"
                                        onChange={(event) => {
                                            setRowsPerPage(Number(event.target.value));
                                            setPage(1);
                                        }}
                                    >
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                        <MenuItem value={100}>100</MenuItem>
                                        <MenuItem value={200}>200</MenuItem>
                                    </Select>
                                </FormControl>
                                {totalPages > 1 && (
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(_, value) => setPage(value)}
                                        color="primary"
                                        size="small"
                                        showFirstButton
                                        showLastButton
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default MediaNameManagement;
